import { Form } from "@/components/ui/form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { MezzaFormField } from "@/components/core/Form/MezzaFormField";
import { MezzaPasswordField } from "@/components/core/Form/MezzaPasswordField";
import { useLoginForm } from "../hooks/useLoginForm";
import { ButtonWithLoading } from "@/components/core/Form/ButtonWithLoading";
import { AuthPageLayout } from "@/components/layout/AuthPageLayout";
import { useAuthPageInteraction } from "@/hooks/useAuthPageInteraction";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { KeyRound, Mail } from "lucide-react";
import { useEffect } from "react";
import { useConfirmPasswordResetMutation } from "@/stores/queries/auth.query";
import { captureException } from "@/utils/sentry";
import { getLoginRedirectFromLocation, LOGIN_REDIRECT_PARAM } from "@/common/routes.common";

export function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { form, handleSubmit, isLoading } = useLoginForm();
  const { hasBeenInteracted, handleFormFocus } = useAuthPageInteraction();
  const { mutate: confirmPasswordReset } = useConfirmPasswordResetMutation();
  const redirect = getLoginRedirectFromLocation(`?${searchParams}`);
  const otpRoute = redirect ? `/login/otp?${new URLSearchParams({ [LOGIN_REDIRECT_PARAM]: redirect })}` : "/login/otp";

  useEffect(() => {
    handlePasswordResetToken();
  }, []);

  const handleForgotPassword = () => {
    const email = form.getValues("email");
    navigate("/forgot-password", { state: { email } });
  };

  const handlePasswordResetToken = () => {
    const token = searchParams.get("token");
    if (!token) return;

    confirmPasswordReset(
      { resetToken: token },
      {
        onError: (error) => {
          captureException(error as Error, {
            extra: {
              component: 'Login',
              action: 'confirmPasswordReset',
            },
          });
        },
      }
    );

    searchParams.delete("token");
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <AuthPageLayout hasBeenInteracted={hasBeenInteracted}>
      <div>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight">{t("auth.signIn")}</h2>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-8 space-y-6">
          <div className="space-y-4">
            <MezzaFormField
              form={form}
              name="email"
              placeholder="auth.email"
              type="email"
              inputMode="email"
              onMouseDown={handleFormFocus}
            />
            <MezzaPasswordField
              form={form}
              name="password"
              placeholder="auth.password"
              onMouseDown={handleFormFocus}
            />
          </div>

          <ButtonWithLoading type="submit" className="w-full" disabled={isLoading} isLoading={isLoading}>
            {t("auth.signIn")}
          </ButtonWithLoading>
        </form>
      </Form>
      <div className="flex flex-col items-center space-y-3">
        <button
          type="button"
          onClick={handleForgotPassword}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <KeyRound className="h-4 w-4" />
          {t("auth.forgotPassword")}
        </button>
      </div>
      {/* Separator with OR text */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <Separator />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">{t("auth.or")}</span>
        </div>
      </div>

      {/* Alternative login options */}
      <div className="flex flex-col items-center space-y-3 mt-1">
        <Button variant="text" className="w-full" asChild>
          <Link to={otpRoute} className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            {t("auth.loginWithOtp")}
          </Link>
        </Button>
      </div>
    </AuthPageLayout>
  );
}
